import type { Plan, PlannedTaskView } from './data';
import { isActiveUsableWorker } from './active-worker-projection';
import type { WorkerChatReadiness } from './worker-chat-readiness';

export type WorkerAvailability = 'available' | 'offline' | 'unavailable' | 'unknown';

export interface WorkerRequestWorkerFact {
  readonly number: number;
  readonly name?: string | null;
  readonly availability: WorkerAvailability;
  readonly runtime?: string | null;
  readonly model?: string | null;
  readonly node?: string | null;
}

export interface WorkerRequestTaskFacts {
  /** Exact files or subsystem the receiving Worker owns, when already known. */
  readonly ownership?: readonly string[];
  /** Explicit do-not-touch boundaries already established for this task. */
  readonly exclusions?: readonly string[];
  /** Expected outputs beyond the standing durable GitHub handoff. */
  readonly deliverables?: readonly string[];
  /** Extra focused checks specifically required for this task. */
  readonly focusedValidation?: readonly string[];
}

export interface WorkerRequestContext {
  readonly repository: string;
  readonly issueNumber?: number | null;
  readonly pullRequestNumber?: number | null;
  readonly currentMainSha?: string | null;
  /** Current Worker facts only. Absence is Unknown; this helper never infers availability or identity. */
  readonly workers?: readonly WorkerRequestWorkerFact[];
  /** Optional task-specific facts keyed by the approved plan task's zero-based index. */
  readonly taskFacts?: Readonly<Record<number, WorkerRequestTaskFacts>>;
}

export interface WorkerRequestBlock {
  readonly taskIndex: number;
  readonly workerNumber: number | null;
  readonly designation: string;
  readonly dependencyTaskIndexes: readonly number[];
  readonly parallelTaskIndexes: readonly number[];
  readonly text: string;
}

const UNKNOWN = 'Unknown';
const NOT_SPECIFIED = 'Not specified in approved plan.';

function clean(value: string): string {
  return value.trim();
}

function presentList(values: readonly string[] | undefined): string {
  if (values === undefined || values.length === 0) return NOT_SPECIFIED;
  return values.map((value) => `- ${clean(value)}`).join('\n');
}

function validDependencies(task: PlannedTaskView, taskIndex: number): number[] {
  return [...(task.dependsOn ?? [])]
    .filter((index) => Number.isInteger(index) && index >= 0 && index < taskIndex)
    .filter((index, position, all) => all.indexOf(index) === position);
}

function dependencyClosure(tasks: readonly PlannedTaskView[], taskIndex: number, seen = new Set<number>()): Set<number> {
  for (const dependency of validDependencies(tasks[taskIndex]!, taskIndex)) {
    if (seen.has(dependency)) continue;
    seen.add(dependency);
    dependencyClosure(tasks, dependency, seen);
  }
  return seen;
}

function parallelTaskIndexes(tasks: readonly PlannedTaskView[], taskIndex: number): number[] {
  const ownDependencies = dependencyClosure(tasks, taskIndex);
  const parallel: number[] = [];
  for (let index = 0; index < tasks.length; index += 1) {
    if (index === taskIndex || ownDependencies.has(index)) continue;
    const otherDependencies = dependencyClosure(tasks, index);
    if (!otherDependencies.has(taskIndex)) parallel.push(index);
  }
  return parallel;
}

function workerFact(context: WorkerRequestContext, number: number | undefined): WorkerRequestWorkerFact | null {
  if (number === undefined) return null;
  return context.workers?.find((worker) => worker.number === number) ?? null;
}

function workerDesignation(task: PlannedTaskView, context: WorkerRequestContext): {
  designation: string;
  destinationWorkerNumber: number | null;
  routingNote: string;
  identityNote: string;
} {
  const preferred = task.preferredWorkerNumber;
  const fact = workerFact(context, preferred);

  if (preferred === undefined) {
    return {
      designation: 'UNASSIGNED WORKER',
      destinationWorkerNumber: null,
      routingNote: 'No Worker was pinned by the approved plan. Keep this request unassigned until an eligible available Worker is chosen.',
      identityNote: 'Runtime/model identity: Unknown. Do not guess or silently substitute a runtime or model.',
    };
  }

  if (fact === null || fact.availability !== 'available') {
    const availability = fact?.availability ?? 'unknown';
    return {
      designation: 'UNASSIGNED WORKER',
      destinationWorkerNumber: null,
      routingNote: `Approved plan preference: Worker ${preferred}. Current availability: ${availability}. Do not present it as an available destination; assign only after current eligibility/availability is proven.`,
      identityNote: 'Runtime/model identity: Unknown for the eventual receiving Worker. Do not guess or silently substitute a runtime or model.',
    };
  }

  const label = clean(fact.name ?? '');
  const designation = label === '' ? `WORKER ${preferred}` : `WORKER ${preferred} — ${label}`;
  const runtime = clean(fact.runtime ?? '');
  const model = clean(fact.model ?? '');
  const node = clean(fact.node ?? '');
  const identityNote = runtime === '' || model === ''
    ? `Runtime/model identity: Unknown${node === '' ? '' : ` on ${node}`}. Do not guess or silently substitute a runtime or model.`
    : `Runtime/model identity: ${runtime} · ${model}${node === '' ? '' : ` · ${node}`}.`;

  return {
    designation,
    destinationWorkerNumber: preferred,
    routingNote: `Approved destination: Worker ${preferred}. Current availability: available.`,
    identityNote,
  };
}

function dependencyLine(tasks: readonly PlannedTaskView[], taskIndex: number): string {
  const dependencies = validDependencies(tasks[taskIndex]!, taskIndex);
  if (dependencies.length === 0) return 'Dependencies: none.';
  return `Dependencies: complete request ${dependencies.map((index) => index + 1).join(', ')} first.`;
}

function parallelLine(tasks: readonly PlannedTaskView[], taskIndex: number): string {
  const parallel = parallelTaskIndexes(tasks, taskIndex);
  if (parallel.length === 0) return 'Parallelism: no other request is proven independent.';
  return `Parallelism: may run in parallel with request ${parallel.map((index) => index + 1).join(', ')}.`;
}

/**
 * Pure formatting bridge for #561. It creates copy text only: no task, assignment,
 * approval, dispatch, RPC, terminal write, store update, or other execution side effect.
 */
export function approvedPlanWorkerRequestBlocks(
  plan: Plan,
  context: WorkerRequestContext,
): WorkerRequestBlock[] {
  if (plan.status !== 'approved') return [];

  return plan.tasks.map((task, taskIndex) => {
    const facts = context.taskFacts?.[taskIndex];
    const worker = workerDesignation(task, context);
    const dependencies = validDependencies(task, taskIndex);
    const parallel = parallelTaskIndexes(plan.tasks, taskIndex);
    const issue = context.issueNumber == null ? UNKNOWN : `#${context.issueNumber}`;
    const pullRequest = context.pullRequestNumber == null ? UNKNOWN : `#${context.pullRequestNumber}`;
    const mainSha = clean(context.currentMainSha ?? '') || UNKNOWN;

    const text = [
      `DESIGNATION: ${worker.designation}`,
      '',
      'OBJECTIVE AND SCOPE',
      clean(task.title),
      clean(task.instructions),
      '',
      'REPOSITORY / ISSUE / PR CONTEXT',
      `Repository: ${clean(context.repository)}`,
      `Issue: ${issue}`,
      `Pull request: ${pullRequest}`,
      `Approved plan: ${plan.id}`,
      `Current main SHA: ${mainSha}${mainSha === UNKNOWN ? ' — re-fetch current main before work.' : ''}`,
      '',
      'WORKER / RUNTIME TRUTH',
      worker.routingNote,
      worker.identityNote,
      '',
      'OWNERSHIP BOUNDARIES',
      'Owned files/subsystem:',
      presentList(facts?.ownership),
      'Explicit exclusions:',
      facts?.exclusions === undefined || facts.exclusions.length === 0
        ? '- Stay within this approved task. No unrelated cleanup; do not overwrite another active lane.'
        : presentList(facts.exclusions),
      '',
      'DEPENDENCIES / PARALLELISM',
      dependencyLine(plan.tasks, taskIndex),
      parallelLine(plan.tasks, taskIndex),
      '',
      'EXPECTED DELIVERABLES',
      facts?.deliverables === undefined || facts.deliverables.length === 0
        ? '- Implement the approved task scope.\n- Leave a durable GitHub handoff with exact changed files, commit SHA, blockers, proof actually observed, and PR URL when applicable.'
        : `${presentList(facts.deliverables)}\n- Leave a durable GitHub handoff with exact changed files, commit SHA, blockers, proof actually observed, and PR URL when applicable.`,
      '',
      'STANDING OWNER RULES / PROOF',
      '- Before any build/edit/implementation work, personally read the current repository root AGENTS.md first and then docs/BUILD-RULES.md. This remains mandatory even when this request came from the Overseer or another AI.',
      '- Do not rely on another AI saying it read the rules, or on copied rule text in this request, a handoff, memory, or older chat. Follow the current files plus later task-specific owner instructions.',
      facts?.focusedValidation === undefined || facts.focusedValidation.length === 0
        ? '- No extra task-specific proof requirement is specified here; use the proof mode required by current AGENTS.md.'
        : `TASK-SPECIFIC PROOF REQUIREMENTS:\n${presentList(facts.focusedValidation)}`,
      '',
      'GITHUB HANDOFF',
      '- Commit only this lane\'s owned changes and open/update the task PR as assigned.',
      '- Report only checks actually observed; never claim tests, build, deployment, or merge passed without evidence.',
    ].join('\n');

    return {
      taskIndex,
      workerNumber: worker.destinationWorkerNumber,
      designation: worker.designation,
      dependencyTaskIndexes: dependencies,
      parallelTaskIndexes: parallel,
      text,
    };
  });
}

/**
 * Current Worker facts from canonical #531/#566 readiness. Only a Worker that
 * `isActiveUsableWorker` proves usable now is `available`; nothing is inferred.
 */
export function workerRequestFactsFromReadiness(
  readiness: readonly WorkerChatReadiness[],
): WorkerRequestWorkerFact[] {
  return readiness.map((worker) => ({
    number: worker.workerNumber,
    name: worker.workerName,
    availability: isActiveUsableWorker(worker)
      ? 'available'
      : worker.machineConnection === 'disconnected' || worker.machineConnection === 'stale'
        ? 'offline'
        : worker.machineConnection === 'unknown' ? 'unknown' : 'unavailable',
    runtime: worker.runtimeLabel ?? worker.runtimeKey,
    model: worker.modelKey,
    node: worker.nodeName,
  }));
}

/** Every block of one packet, in plan order, for a single Copy all. */
export function workerRequestPacketText(blocks: readonly WorkerRequestBlock[]): string {
  return blocks
    .map((block) => `REQUEST ${block.taskIndex + 1} OF ${blocks.length}\n${block.text}`)
    .join('\n\n----------------------------------------\n\n');
}
